<template>
  <div class="statistic-container">
    <div class="left-content">
      <!-- 第一块：饼状图区域 -->
      <div class="dashboard-card">
        <div class="card-header">
          <h3>测试车辆统计</h3>
        </div>
        <div class="pie-charts-container">
          <!-- 企业测试车辆统计 -->
          <div class="pie-chart-item">
            <h4>企业测试车辆统计</h4>
            <div ref="enterprisePieChartRef" class="chart-container"></div>
          </div>
          <!-- 测试车辆类型统计 -->
          <div class="pie-chart-item">
            <h4>测试车辆类型统计</h4>
            <div ref="vehicleTypePieChartRef" class="chart-container"></div>
          </div>
        </div>
      </div>

      <!-- 第二块：柱状图区域 -->
      <div class="dashboard-card">
        <div class="card-header">
          <h3>企业测试里程统计</h3>
          <div class="time-selector">
            <a-radio-group v-model:value="timeRangeType" @change="handleTimeRangeChange">
              <a-radio-button value="week">本周</a-radio-button>
              <a-radio-button value="month">本月</a-radio-button>
              <a-radio-button value="year">本年</a-radio-button>
            </a-radio-group>
            <a-date-picker
              v-model="customDateRange"
              type="range"
              format="YYYY-MM-DD"
              style="margin-left: 10px"
              @change="handleCustomDateChange"
            />
          </div>
        </div>
        <div ref="mileageBarChartRef" class="chart-container"></div>
      </div>
    </div>

    <div class="right-content">
      <!-- 企业车辆测试排名 -->
      <div class="dashboard-card">
        <div class="card-header">
          <h3>企业车辆测试排名</h3>
        </div>
        <div class="table-container">
          <a-table :columns="rankColumns" :data-source="rankData" :pagination="false">
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'mileage'">
                {{ record.mileage.toLocaleString() }} km
              </template>
            </template>
          </a-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue'
import * as echarts from 'echarts'
import { Radio, DatePicker, Table } from 'ant-design-vue'
import dayjs from 'dayjs'

// 时间范围类型
const timeRangeType = ref('month')
const customDateRange = ref([dayjs().subtract(30, 'day'), dayjs()])

// 图表容器引用
const enterprisePieChartRef = ref(null)
const vehicleTypePieChartRef = ref(null)
const mileageBarChartRef = ref(null)

// 图表实例
let enterprisePieChart = null
let vehicleTypePieChart = null
let mileageBarChart = null

// 企业测试车辆统计数据
const enterpriseVehicleData = [
  { name: '企业A', value: 50 },
  { name: '企业B', value: 30 },
  { name: '企业C', value: 20 },
  { name: '企业D', value: 15 },
  { name: '企业E', value: 10 }
]

// 测试车辆类型统计数据
const vehicleTypeData = [
  { name: '轿车', value: 60 },
  { name: 'SUV', value: 30 },
  { name: '卡车', value: 10 }
]

// 企业测试里程数据
const enterpriseMileageData = [
  { name: '企业A', mileage: 12500 },
  { name: '企业B', mileage: 9800 },
  { name: '企业C', mileage: 7200 },
  { name: '企业D', mileage: 5600 },
  { name: '企业E', mileage: 4300 }
]

// 企业车辆测试排名数据
const rankData = [
  { key: '1', enterprise: '企业A', routes: 25, vehicles: 50, mileage: 12500 },
  { key: '2', enterprise: '企业B', routes: 18, vehicles: 30, mileage: 9800 },
  { key: '3', enterprise: '企业C', routes: 15, vehicles: 20, mileage: 7200 },
  { key: '4', enterprise: '企业D', routes: 12, vehicles: 15, mileage: 5600 },
  { key: '5', enterprise: '企业E', routes: 10, vehicles: 10, mileage: 4300 }
]

// 排名表格列定义
const rankColumns = [
  { title: '企业名称', dataIndex: 'enterprise', key: 'enterprise' },
  { title: '测试/运营路线数', dataIndex: 'routes', key: 'routes' },
  { title: '车辆数', dataIndex: 'vehicles', key: 'vehicles' },
  { title: '测试里程', dataIndex: 'mileage', key: 'mileage' }
]

// 初始化企业测试车辆统计饼状图
const initEnterprisePieChart = () => {
  if (!enterprisePieChartRef.value) return
  
  enterprisePieChart = echarts.init(enterprisePieChartRef.value)
  const option = {
    tooltip: {
      trigger: 'item',
      formatter: '{b}: {c} 辆 ({d}%)'
    },
    legend: {
      orient: 'vertical',
      left: 'left',
      textStyle: {
        color: '#666666'
      }
    },
    series: [
      {
        name: '企业测试车辆',
        type: 'pie',
        radius: '60%',
        data: enterpriseVehicleData,
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        }
      }
    ]
  }
  enterprisePieChart.setOption(option)
}

// 初始化测试车辆类型统计饼状图
const initVehicleTypePieChart = () => {
  if (!vehicleTypePieChartRef.value) return
  
  vehicleTypePieChart = echarts.init(vehicleTypePieChartRef.value)
  const option = {
    tooltip: {
      trigger: 'item',
      formatter: '{b}: {c} 辆 ({d}%)'
    },
    legend: {
      orient: 'vertical',
      left: 'left',
      textStyle: {
        color: '#666666'
      }
    },
    series: [
      {
        name: '测试车辆类型',
        type: 'pie',
        radius: '60%',
        data: vehicleTypeData,
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        }
      }
    ]
  }
  vehicleTypePieChart.setOption(option)
}

// 初始化企业测试里程统计柱状图
const initMileageBarChart = () => {
  if (!mileageBarChartRef.value) return
  
  mileageBarChart = echarts.init(mileageBarChartRef.value)
  const option = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      formatter: function(params) {
        return params[0].name + '<br/>' + params[0].marker + '测试里程: ' + params[0].value + ' km'
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: enterpriseMileageData.map(item => item.name),
      axisLabel: {
        color: '#666666',
        fontSize: 12
      },
      axisLine: {
        lineStyle: {
          color: 'rgba(0, 0, 0, 0.2)'
        }
      },
      axisTick: {
        show: false
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        color: '#666666',
        fontSize: 12,
        formatter: '{value} km'
      },
      axisLine: {
        lineStyle: {
          color: 'rgba(0, 0, 0, 0.2)'
        }
      },
      splitLine: {
        lineStyle: {
          color: 'rgba(0, 0, 0, 0.1)'
        }
      }
    },
    series: [
      {
        name: '测试里程',
        type: 'bar',
        data: enterpriseMileageData.map(item => item.mileage),
        itemStyle: {
          color: '#5CACEE',
          borderRadius: [4, 4, 0, 0]
        },
        barWidth: '60%'
      }
    ]
  }
  mileageBarChart.setOption(option)
}

// 处理时间范围变化
const handleTimeRangeChange = () => {
  // 这里可以根据时间范围类型更新图表数据
  updateCharts()
}

// 处理时间范围变化
const handleCustomDateChange = (dates) => {
  if (dates) {
    console.log('选择的时间范围:', dates[0].format('YYYY-MM-DD'), '至', dates[1].format('YYYY-MM-DD'))
  }
  // 这里可以根据自定义时间范围更新图表数据
  updateCharts()
}

// 更新所有图表
const updateCharts = () => {
  initEnterprisePieChart()
  initVehicleTypePieChart()
  initMileageBarChart()
}

// 处理窗口大小变化
const handleResize = () => {
  if (enterprisePieChart) {
    enterprisePieChart.resize()
  }
  if (vehicleTypePieChart) {
    vehicleTypePieChart.resize()
  }
  if (mileageBarChart) {
    mileageBarChart.resize()
  }
}

// 组件挂载后初始化图表
onMounted(() => {
  // 延迟初始化图表，确保DOM已渲染
  setTimeout(() => {
    initEnterprisePieChart()
    initVehicleTypePieChart()
    initMileageBarChart()
  }, 100)

  // 添加窗口大小变化监听
  window.addEventListener('resize', handleResize)
})

// 组件卸载前销毁图表实例
onBeforeUnmount(() => {
  if (enterprisePieChart) {
    enterprisePieChart.dispose()
    enterprisePieChart = null
  }
  if (vehicleTypePieChart) {
    vehicleTypePieChart.dispose()
    vehicleTypePieChart = null
  }
  if (mileageBarChart) {
    mileageBarChart.dispose()
    mileageBarChart = null
  }

  // 移除窗口大小变化监听
  window.removeEventListener('resize', handleResize)
})
</script>

<style scoped lang="less">
.statistic-container {
  display: flex;
  gap: 16px;
  padding: 16px;
  background: #ffffff;
  min-height: 100vh;
}

.left-content {
  flex: 0 0 70%;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.right-content {
  flex: 0 0 30%;
}

// 统一卡片组件样式
.dashboard-card {
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid rgba(92, 172, 238, 0.3);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 16px;
  height: auto;
  min-height: 120px;
  position: relative;
  display: block;
  overflow: visible;
  width: 100%;
  box-sizing: border-box;
}

// 卡片标题
.card-header {
  margin-bottom: 16px;
  padding-bottom: 8px;
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333333;
  display: inline-block;
}

// 饼状图容器
.pie-charts-container {
  display: flex;
  gap: 16px;
  height: 300px;
}

.pie-chart-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.pie-chart-item h4 {
  margin: 0 0 16px 0;
  font-size: 14px;
  font-weight: 500;
  color: #666666;
}

// 图表容器
.chart-container {
  width: 100%;
  height: 100%;
  min-height: 250px;
}

// 时间选择器
.time-selector {
  display: flex;
  align-items: center;
}

// 表格容器
.table-container {
  width: 100%;
  overflow-x: auto;
}

// 响应式调整
@media (max-width: 1200px) {
  .statistic-container {
    flex-direction: column;
  }
  
  .left-content,
  .right-content {
    flex: 1;
    width: 100%;
  }
  
  .pie-charts-container {
    flex-direction: column;
    height: auto;
  }
  
  .pie-chart-item {
    height: 300px;
  }
}
</style>